/*
 * EB · Asesor capilar (custom element <eb-pack-wizard>).
 *
 * Reemplaza al wizard de 3 ramas por un cuestionario completo que reproduce
 * el flujo de chat de Elena. Estructura:
 *   - 6 preguntas core + 2 condicionales + textarea opcional
 *   - Multiselect, single, textarea
 *   - showIf para condicionales (ej. tinte_recencia sólo si tinte=quimico|mechas)
 *   - Motor de recomendación que produce 4 niveles: básico, intermedio, pack, super
 *   - Cards finales con add-to-cart + redirect a /discount/CODE?redirect=/cart
 *
 * Las líneas NO llevan _pack_grupo (evita conflicto con cupón manual y bug
 * conocido de la Function eb-discount-engine).
 */
(function () {
  if (customElements.get('eb-pack-wizard')) return;

  const RESULTS_TITLE_TPL = 'Por lo que me cuentas...';

  class EBPackWizard extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.dataset.config || '{}');
        this.catalog = JSON.parse(this.dataset.catalog || '{}');
      } catch (_e) {
        this.config = { questions: [], discounts: {} };
        this.catalog = {};
      }
      this.questions = Array.isArray(this.config.questions) ? this.config.questions : [];
      this.answers = {};
      this.currentIndex = 0;
      this.finished = false;

      this.progressEl = this.querySelector('[data-eb-pw-progress]');
      this.progressFill = this.querySelector('[data-eb-pw-progress-fill]');
      this.progressLabel = this.querySelector('[data-eb-pw-progress-label]');
      this.questionWrap = this.querySelector('[data-eb-pw-question-wrap]');
      this.navEl = this.querySelector('[data-eb-pw-nav]');
      this.nextBtn = this.querySelector('[data-eb-pw-next]');
      this.skipBtn = this.querySelector('[data-eb-pw-skip]');
      this.backBtn = this.querySelector('[data-eb-pw-back]');
      this.resultsEl = this.querySelector('[data-eb-pw-results]');
      this.resultsTitle = this.querySelector('[data-eb-pw-results-title]');
      this.resultsSummary = this.querySelector('[data-eb-pw-results-summary]');
      this.cardsEl = this.querySelector('[data-eb-pw-cards]');
      this.statusEl = this.querySelector('[data-eb-pw-status]');
      this.restartBtn = this.querySelector('[data-eb-pw-restart]');

      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.advance());
      if (this.skipBtn) this.skipBtn.addEventListener('click', () => this.advance(true));
      if (this.backBtn) this.backBtn.addEventListener('click', () => this.back());
      if (this.restartBtn) this.restartBtn.addEventListener('click', () => this.restart());

      this.render();
    }

    visibleQuestions() {
      return this.questions.filter((q) => this.shouldShow(q));
    }

    shouldShow(q) {
      if (!q.showIf) return true;
      for (const key in q.showIf) {
        const required = q.showIf[key];
        const got = this.answers[key];
        if (Array.isArray(required)) {
          if (Array.isArray(got)) {
            if (!got.some((v) => required.includes(v))) return false;
          } else {
            if (!required.includes(got)) return false;
          }
        }
      }
      return true;
    }

    currentQuestion() {
      const visible = this.visibleQuestions();
      return visible[this.currentIndex] || null;
    }

    answerOf(qid) {
      return this.answers[qid];
    }

    isAnswered(q) {
      const a = this.answers[q.id];
      if (q.optional) return true;
      if (q.type === 'multiselect') {
        const min = q.min || 1;
        return Array.isArray(a) && a.length >= min;
      }
      if (q.type === 'textarea') return true;
      return a !== undefined && a !== null && a !== '';
    }

    render() {
      if (this.finished) return this.renderResults();

      const visible = this.visibleQuestions();
      if (visible.length === 0) return;
      if (this.currentIndex >= visible.length) {
        this.finished = true;
        return this.renderResults();
      }

      const q = visible[this.currentIndex];

      // Progress
      if (this.progressEl) this.progressEl.removeAttribute('hidden');
      const pct = Math.round(((this.currentIndex) / visible.length) * 100);
      if (this.progressFill) this.progressFill.style.width = pct + '%';
      if (this.progressLabel) {
        this.progressLabel.textContent = 'Paso ' + (this.currentIndex + 1) + ' de ' + visible.length;
      }

      // Back btn
      if (this.backBtn) this.backBtn.toggleAttribute('hidden', this.currentIndex === 0);

      // Hide results
      if (this.resultsEl) this.resultsEl.setAttribute('hidden', '');

      // Render question
      this.questionWrap.innerHTML = this.questionTpl(q);
      this.bindQuestion(q);

      // Nav
      if (this.navEl) this.navEl.removeAttribute('hidden');
      if (this.skipBtn) this.skipBtn.toggleAttribute('hidden', !q.optional || q.type === 'textarea');
      this.updateNextState(q);
    }

    questionTpl(q) {
      let html = '<div class="eb-pw__question-block">';
      html += '<h2 class="eb-pw__question">' + escapeHtml(q.title || '') + '</h2>';
      if (q.subtitle) html += '<p class="eb-pw__question-sub">' + escapeHtml(q.subtitle) + '</p>';

      if (q.type === 'textarea') {
        const val = this.answers[q.id] || '';
        const max = q.maxLength || 300;
        html += '<textarea class="eb-pw__textarea" data-eb-input maxlength="' + max + '" placeholder="' + escapeHtml('Cuéntame...') + '">' + escapeHtml(val) + '</textarea>';
      } else {
        const isMulti = q.type === 'multiselect';
        html += '<div class="eb-pw__options" role="' + (isMulti ? 'group' : 'radiogroup') + '">';
        const selected = this.answers[q.id];
        for (const c of (q.choices || [])) {
          const isSel = isMulti
            ? Array.isArray(selected) && selected.includes(c.id)
            : selected === c.id;
          html += '<button type="button" class="eb-pw__choice" data-choice="' + escapeAttr(c.id) + '"';
          html += ' role="' + (isMulti ? 'checkbox' : 'radio') + '"';
          html += ' aria-pressed="' + (isSel ? 'true' : 'false') + '"';
          if (isSel) html += ' data-selected="true"';
          if (c.exclusive) html += ' data-exclusive="true"';
          html += '>';
          html += '<span class="eb-pw__choice-title">' + escapeHtml(c.label || c.id) + '</span>';
          if (c.subtitle) html += '<span class="eb-pw__choice-sub">' + escapeHtml(c.subtitle) + '</span>';
          html += '</button>';
        }
        html += '</div>';
      }
      html += '</div>';
      return html;
    }

    bindQuestion(q) {
      if (q.type === 'textarea') {
        const input = this.questionWrap.querySelector('[data-eb-input]');
        if (input) {
          input.addEventListener('input', (ev) => {
            this.answers[q.id] = ev.target.value;
            this.updateNextState(q);
          });
        }
        return;
      }
      const buttons = this.questionWrap.querySelectorAll('[data-choice]');
      buttons.forEach((btn) => {
        btn.addEventListener('click', () => this.toggleChoice(q, btn.dataset.choice));
      });
    }

    toggleChoice(q, choiceId) {
      if (q.type === 'multiselect') {
        const current = Array.isArray(this.answers[q.id]) ? this.answers[q.id].slice() : [];
        const choice = (q.choices || []).find((c) => c.id === choiceId);
        const wasIn = current.indexOf(choiceId);

        if (choice && choice.exclusive) {
          // Click on exclusive: clear all and set just this (or unset)
          this.answers[q.id] = wasIn >= 0 ? [] : [choiceId];
        } else {
          // Click on regular: remove any exclusive, toggle this one
          let next = current.filter((id) => {
            const c = (q.choices || []).find((cc) => cc.id === id);
            return !(c && c.exclusive);
          });
          const nextIdx = next.indexOf(choiceId);
          if (nextIdx >= 0) next.splice(nextIdx, 1);
          else next.push(choiceId);
          this.answers[q.id] = next;
        }
      } else {
        const wasSel = this.answers[q.id] === choiceId;
        this.answers[q.id] = wasSel ? null : choiceId;
        // Auto-advance for single-choice (better UX)
        if (!wasSel) {
          setTimeout(() => this.advance(), 240);
        }
      }
      // Re-render this question to reflect selection state
      const wrap = this.questionWrap;
      wrap.innerHTML = this.questionTpl(q);
      this.bindQuestion(q);
      this.updateNextState(q);
    }

    updateNextState(q) {
      if (!this.nextBtn) return;
      this.nextBtn.disabled = !this.isAnswered(q);
      // Last question?
      const visible = this.visibleQuestions();
      const isLast = this.currentIndex === visible.length - 1;
      this.nextBtn.textContent = isLast ? 'ver mis recomendaciones →' : 'siguiente →';
    }

    advance(skip) {
      const q = this.currentQuestion();
      if (!q) return;
      if (!skip && !this.isAnswered(q)) return;
      if (skip && q.optional && q.type === 'multiselect') {
        this.answers[q.id] = [];
      }
      // Skipping condicionales: clear answer if showIf no longer holds
      this.currentIndex += 1;
      const visible = this.visibleQuestions();
      if (this.currentIndex >= visible.length) {
        this.finished = true;
        this.renderResults();
        return;
      }
      this.render();
    }

    back() {
      if (this.finished) {
        this.finished = false;
        this.render();
        return;
      }
      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.render();
      }
    }

    restart() {
      this.answers = {};
      this.currentIndex = 0;
      this.finished = false;
      if (this.statusEl) {
        this.statusEl.textContent = '';
        this.statusEl.className = 'eb-pw__status';
      }
      this.render();
    }

    /* ============================================================
       Recommendation engine
       ============================================================ */

    decide() {
      const a = this.answers;
      const objetivos = a.objetivos || [];
      const cuero = a.cuero;
      const fibra = a.fibra;
      const tinte = a.tinte;
      const tinteRec = a.tinte_recencia;
      const textura = a.textura || [];
      const forma = a.forma;
      const caidaNivel = a.caida_nivel;

      // Champú principal — tres reglas en orden de precedencia:
      // 1. Caída severa o alopecia → Revital (Elena: "casos agudos empiezan por Revital")
      // 2. Cuero graso → Revital
      // 3. Resto → Nutri (cubre cuero seco, sensible, fibra seca, normal)
      let champu = 'nutri';
      const caidaSevera = objetivos.includes('caida') && (caidaNivel === 'constante' || caidaNivel === 'alopecia');
      if (caidaSevera) {
        champu = 'revital';
      } else if (cuero === 'graso' || fibra === 'puntas_secas') {
        champu = 'revital';
      }

      const champuAlterno = champu === 'nutri' ? 'revital' : 'nutri';

      // Complementarios
      const complementarios = [];
      const reasons = {};

      const hayCaida = objetivos.includes('caida');
      const haySequedad = objetivos.includes('sequedad');
      const hayCueroReact = objetivos.includes('cuero_reactivo');
      const hayVolumen = objetivos.includes('volumen');
      const hayPeinarme = objetivos.includes('peinarme');
      const hayNatural = objetivos.includes('natural');

      const hayFrizz = textura.includes('encrespa') || textura.includes('frizz') || textura.includes('enreda');
      const hayTinteAgresivo = tinte === 'quimico' || tinte === 'mechas';

      // No Frizz: encrespamiento, fibra seca, picor en cuero, tinte agresivo
      if (hayFrizz || haySequedad || fibra === 'seca' || hayTinteAgresivo) {
        complementarios.push('no_frizz');
        if (hayFrizz) reasons.no_frizz = 'el frizz y el enredo';
        else if (fibra === 'seca' || haySequedad) reasons.no_frizz = 'esa sequedad';
        else reasons.no_frizz = 'el daño del tinte químico';
      }

      // Shungit: caída de cualquier nivel, cuero reactivo, alopecia, hormonal
      if (hayCaida || hayCueroReact || cuero === 'sensible') {
        complementarios.push('shungit');
        if (hayCaida) reasons.shungit = 'la caída';
        else reasons.shungit = 'la sensibilidad del cuero cabelludo';
      }

      // Dynamic: volumen, alopecia, complemento anti-caída intenso
      if (hayVolumen || caidaNivel === 'alopecia' || (hayCaida && (caidaNivel === 'constante'))) {
        complementarios.push('dynamic');
        if (hayVolumen) reasons.dynamic = 'el volumen';
        else reasons.dynamic = 'estimular el cuero cabelludo';
      }

      // Care Fix: rizos/ondas + peinarme, o styling explícito
      if (hayPeinarme && (forma === 'ondulado' || forma === 'rizado')) {
        complementarios.push('care_fix');
        reasons.care_fix = 'fijar tu rizo sin apelmazar';
      } else if (hayPeinarme && !complementarios.includes('care_fix') && complementarios.length < 3) {
        complementarios.push('care_fix');
        reasons.care_fix = 'fijar el peinado sin apelmazar';
      }

      // Kashmir: para pack si peinarme + rizado/ondulado (acabado definido)
      const kashmirParaPack = hayPeinarme && (forma === 'ondulado' || forma === 'rizado');

      // "Pasarme a natural" sin más señales: igualmente proponer rutina base
      // (no_frizz cubre 80% de los casos universales, shungit prepara cuero)
      if (hayNatural && complementarios.length === 0) {
        complementarios.push('no_frizz');
        reasons.no_frizz = 'redondear tu rutina natural';
        complementarios.push('shungit');
        reasons.shungit = 'preparar el cuero cabelludo para el cambio';
      }

      // Dedup
      const uniq = (arr) => Array.from(new Set(arr));

      // Prioridad cuando hay que recortar a 1 complementario para nivel 2:
      //   1. shungit (si caída)
      //   2. no_frizz (si sequedad o tinte)
      //   3. dynamic (si volumen)
      //   4. care_fix
      const pickFirstComp = (() => {
        if (hayCaida && complementarios.includes('shungit')) return 'shungit';
        if ((haySequedad || hayTinteAgresivo || hayFrizz) && complementarios.includes('no_frizz')) return 'no_frizz';
        if (hayVolumen && complementarios.includes('dynamic')) return 'dynamic';
        return complementarios[0] || null;
      })();

      // Niveles
      const basico = [champu];
      const intermedio = pickFirstComp ? uniq([champu, pickFirstComp]) : null;

      // Pack: champú + todos los complementarios (cap razonable para no saturar la card)
      let packItems = uniq([champu, ...complementarios]);
      // Si peinarme + rizado/ondulado, kashmir va dentro del pack para tener
      // styling completo (no solo en super). Cabe siempre que <5 items.
      if (kashmirParaPack && !packItems.includes('kashmir') && packItems.length < 5) {
        packItems.push('kashmir');
      }
      packItems = packItems.slice(0, 5);

      // Mostrar pack solo si tiene sentido (no duplicar el intermedio)
      const showPack = packItems.length >= 3
        || (hayNatural && packItems.length >= 2)
        || (hayCaida && packItems.length >= 2)
        || (hayPeinarme && packItems.length >= 2);
      const pack = showPack ? packItems : null;

      // Super: pack + champú alterno (+ kashmir si peinarme rizado y no estaba ya)
      let superSet = pack ? uniq([...pack, champuAlterno]) : null;
      if (superSet && kashmirParaPack && !superSet.includes('kashmir')) {
        superSet.push('kashmir');
      }
      // Super se muestra si añade valor real al pack (al menos +1 producto)
      const showSuper = superSet && pack && superSet.length > pack.length
        && (pack.length >= 3 || hayNatural);
      const superPack = showSuper ? superSet : null;

      return {
        profile: a,
        champu,
        champuAlterno,
        complementarios,
        reasons,
        levels: {
          basico,
          intermedio,
          pack,
          super: superPack,
        },
      };
    }

    /* ============================================================
       Results UI
       ============================================================ */

    renderResults() {
      const decision = this.decide();
      this.lastDecision = decision;

      // Hide question wrap, nav, progress
      if (this.questionWrap) this.questionWrap.innerHTML = '';
      if (this.navEl) this.navEl.setAttribute('hidden', '');
      if (this.progressEl) this.progressEl.setAttribute('hidden', '');
      if (this.backBtn) this.backBtn.removeAttribute('hidden');

      // Show results
      if (this.resultsEl) this.resultsEl.removeAttribute('hidden');

      if (this.resultsTitle) this.resultsTitle.textContent = RESULTS_TITLE_TPL;
      if (this.resultsSummary) {
        this.resultsSummary.textContent = this.summaryNarrative(decision);
      }

      this.cardsEl.innerHTML = '';
      const discounts = this.config.discounts || {};

      // Always show básico
      this.cardsEl.appendChild(this.cardEl({
        tier: 'basico',
        kicker: 'Lo básico',
        title: 'Tu champú: ' + this.catalog[decision.champu].shortTitle,
        narrative: this.basicoNarrative(decision),
        items: decision.levels.basico,
        discount: null,
        cta: 'Añadir champú al carrito',
        disclaimer: 'Por compras superiores a 35€ te enviamos muestras de regalo, automáticamente.',
      }));

      // Intermedio
      if (decision.levels.intermedio) {
        const compId = decision.levels.intermedio[1];
        const compTitle = this.catalog[compId].shortTitle;
        this.cardsEl.appendChild(this.cardEl({
          tier: 'intermedio',
          kicker: 'Lo que te recomiendo añadir',
          title: this.catalog[decision.champu].shortTitle + ' + ' + compTitle,
          narrative: this.intermedioNarrative(decision),
          items: decision.levels.intermedio,
          discount: discounts.intermedio,
          cta: 'Llevarme los dos · ' + (discounts.intermedio ? discounts.intermedio.label : ''),
          disclaimer: 'Cupón aplicado al añadir al carrito. No combinable con EKO10.',
        }));
      }

      // Pack
      if (decision.levels.pack) {
        this.cardsEl.appendChild(this.cardEl({
          tier: 'pack',
          kicker: 'El pack completo · lo que yo haría',
          title: 'Tu rutina completa',
          narrative: this.packNarrative(decision),
          items: decision.levels.pack,
          discount: discounts.pack,
          cta: 'Llevarme el pack · ' + (discounts.pack ? discounts.pack.label : ''),
          disclaimer: 'Cupón aplicado al añadir al carrito. No combinable con EKO10. Si tu pedido pasa de 35€, además te llevas muestras de regalo.',
        }));
      }

      // Super
      if (decision.levels.super) {
        this.cardsEl.appendChild(this.cardEl({
          tier: 'super',
          kicker: 'Y si quieres ir a por todas',
          title: 'El kit EB completo',
          narrative: this.superNarrative(decision),
          items: decision.levels.super,
          discount: discounts.super,
          cta: 'Llevarme todo · ' + (discounts.super ? discounts.super.label : ''),
          disclaimer: 'Cupón aplicado al añadir al carrito. No combinable con EKO10. Pasarás holgadamente de 70€-100€, así que también te lleves regalos automáticos.',
        }));
      }
    }

    cardEl({ tier, kicker, title, narrative, items, discount, cta, disclaimer }) {
      const card = document.createElement('article');
      card.className = 'eb-pw__card';
      card.setAttribute('data-tier', tier);

      const total = items.reduce((sum, id) => sum + (this.catalog[id]?.price || 0), 0);
      const totalEur = (total / 100).toFixed(2).replace('.', ',') + ' €';

      let saving = null;
      let after = null;
      if (discount) {
        if (discount.type === 'percentage') {
          saving = total * (discount.value / 100);
          after = total - saving;
        } else if (discount.type === 'fixed') {
          saving = discount.value * 100; // value en €, total en céntimos
          after = total - saving;
        }
      }
      const savingEur = saving != null ? (saving / 100).toFixed(2).replace('.', ',') + ' €' : null;
      const afterEur = after != null ? (after / 100).toFixed(2).replace('.', ',') + ' €' : null;

      card.innerHTML = `
        <span class="eb-pw__card-kicker">${escapeHtml(kicker)}</span>
        <h3 class="eb-pw__card-title">${escapeHtml(title)}</h3>
        <p class="eb-pw__card-narrative">${escapeHtml(narrative)}</p>
        <div class="eb-pw__card-products">
          ${items.map((id) => {
            const p = this.catalog[id];
            if (!p) return '';
            return `<a class="eb-pw__chip" href="${escapeAttr(p.url)}" target="_blank" rel="noopener">
              ${p.image ? `<img src="${escapeAttr(p.image)}" alt="" loading="lazy">` : ''}
              <span>${escapeHtml(p.shortTitle || p.title)}</span>
            </a>`;
          }).join('')}
        </div>
        <div class="eb-pw__card-meta">
          <span class="eb-pw__card-price">
            ${discount
              ? `<s>${totalEur}</s> <strong>${afterEur}</strong>`
              : `<strong>${totalEur}</strong>`}
          </span>
          ${discount ? `<span class="eb-pw__card-discount">ahorras ${savingEur} con ${discount.code}</span>` : ''}
        </div>
        <button type="button" class="eb-pw__card-cta" data-eb-pw-add data-tier="${escapeAttr(tier)}">
          ${escapeHtml(cta)}
        </button>
        ${disclaimer ? `<p class="eb-pw__card-disclaimer">${escapeHtml(disclaimer)}</p>` : ''}
      `;

      const ctaBtn = card.querySelector('[data-eb-pw-add]');
      if (ctaBtn) ctaBtn.addEventListener('click', () => this.handleAddToCart(tier, items, discount));

      return card;
    }

    summaryNarrative(d) {
      const a = d.profile;
      const partes = [];
      if (a.cuero === 'seco' || a.cuero === 'sensible') partes.push('cuero cabelludo seco');
      if (a.cuero === 'graso') partes.push('cuero cabelludo graso');
      if (a.fibra === 'seca') partes.push('pelo seco o quebradizo');
      if (a.fibra === 'puntas_secas') partes.push('puntas castigadas');
      if ((a.objetivos || []).includes('caida')) partes.push('caída');
      if ((a.textura || []).includes('frizz') || (a.textura || []).includes('encrespa')) partes.push('encrespamiento');
      if ((a.objetivos || []).includes('volumen')) partes.push('necesidad de volumen');
      if (a.tinte === 'quimico' || a.tinte === 'mechas') partes.push('fibra dañada por tinte químico');

      const champTitle = this.catalog[d.champu]?.shortTitle || d.champu;
      const partsTxt = partes.length
        ? 'tienes ' + partes.slice(0, 3).join(', ')
        : 'lo que necesitas es una rutina sencilla y de fondo';
      return 'Por lo que me cuentas, ' + partsTxt + '. Tu champú base es ' + champTitle + '. Te dejo aquí mi recomendación en tres niveles, según hasta dónde quieras llegar.';
    }

    basicoNarrative(d) {
      const champTitle = this.catalog[d.champu]?.shortTitle || d.champu;
      const reason = d.champu === 'nutri'
        ? 'Es el más nutritivo, perfecto si tu cuero o tu pelo van algo secos.'
        : 'Equilibra el cuero cabelludo y limpia en profundidad sin agredir.';
      return 'Solamente con cambiar de champú a ' + champTitle + ' deberías de notar la diferencia, ya verás. ' + reason + ' Si quieres empezar suave, este es el primer paso.';
    }

    intermedioNarrative(d) {
      const compId = d.levels.intermedio[1];
      const compTitle = this.catalog[compId]?.shortTitle || compId;
      const reason = d.reasons[compId] || 'tu caso';
      let narr = 'Pero en tu caso, te vendría estupendamente añadir ' + compTitle + ' para ' + reason + '. ';
      if (compId === 'shungit') {
        narr += 'Es un tratamiento de 10 semanas (un sobre semanal) que va dentro del cuero cabelludo, no es solo para tener mejor pelo, lo trata realmente.';
      } else if (compId === 'no_frizz') {
        narr += 'No es un acondicionador cualquiera: hace acondicionador, leave-in y mascarilla. Y multiplica el efecto del champú.';
      } else if (compId === 'dynamic') {
        narr += 'Además del volumen, te hace tratamiento — es un tónico capilar bastante potente.';
      } else if (compId === 'care_fix') {
        narr += 'No es solo para fijar — los productos de peinado nuestros también son tratantes, en perfecta sinergia con el champú.';
      }
      return narr;
    }

    packNarrative(d) {
      const items = d.levels.pack || [];
      const restTitles = items.slice(1).map((id) => this.catalog[id]?.shortTitle || id);
      let narr = 'Si quieres pasarte de una vez por todas a lo natural, sin medias tintas, este es el pack que yo te montaría: ';
      narr += this.catalog[d.champu]?.shortTitle + ' como base, y luego ' + restTitles.join(', ') + '. ';
      narr += 'Estos productos en sinergia trabajan distinto a sumarlos sueltos — verás los cambios en pocas semanas.';
      return narr;
    }

    superNarrative(d) {
      const altTitle = this.catalog[d.champuAlterno]?.shortTitle || d.champuAlterno;
      let narr = 'Te añado el otro champú, ' + altTitle + ', porque hemos visto que a mucha gente le viene bien alternarlos según la época, las hormonas o las estaciones. ';
      narr += 'Es lo más completo que te puedo recomendar — no porque sea más caro, sino porque cubre todos los frentes.';
      return narr;
    }

    /* ============================================================
       Add to cart + apply discount
       ============================================================ */

    async handleAddToCart(tier, itemIds, discount) {
      const buttons = this.cardsEl.querySelectorAll('[data-eb-pw-add]');
      buttons.forEach((b) => { b.disabled = true; });
      const myBtn = this.cardsEl.querySelector('[data-eb-pw-add][data-tier="' + tier + '"]');
      const originalText = myBtn ? myBtn.textContent : '';
      if (myBtn) myBtn.textContent = 'Añadiendo...';

      try {
        const items = itemIds.map((id) => {
          const variantId = this.catalog[id]?.variantId;
          if (!variantId) throw new Error('Variant no resuelta para ' + id);
          return {
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: {
              _quizz_origen: tier,
              _quizz_at: new Date().toISOString().slice(0, 10),
            },
          };
        });

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'No se pudo añadir al carrito');
        }
        await res.json();

        if (window.EBCart && typeof window.EBCart.refresh === 'function') {
          await window.EBCart.refresh('eb-pack-wizard');
        }
        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: { source: 'eb-pack-wizard', data: { source: 'eb-pack-wizard', tier } }
        }));
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { source: 'eb-pack-wizard', data: { source: 'eb-pack-wizard', tier } }
        }));

        // Persistir telemetría ligera (útil después)
        try {
          sessionStorage.setItem('eb_quizz_outcome', JSON.stringify({
            tier,
            answers: this.answers,
            items: itemIds,
            discount: discount ? discount.code : null,
            ts: Date.now(),
          }));
        } catch (_e) { /* ignore */ }

        if (discount && discount.code) {
          // Redirige a la URL nativa de Shopify que aplica el cupón al cart
          window.location.href = '/discount/' + encodeURIComponent(discount.code) + '?redirect=/cart';
          return;
        }

        // Sin cupón: abrir cart drawer, navegar al cart si no hay drawer
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.open === 'function') {
          try {
            drawer.open();
          } catch (_e) {
            window.location.href = '/cart';
          }
        } else {
          window.location.href = '/cart';
        }

        if (this.statusEl) {
          this.statusEl.textContent = 'Tu pedido ya está en el carrito ✓';
          this.statusEl.className = 'eb-pw__status eb-pw__status--ok';
        }
        buttons.forEach((b) => { b.disabled = false; });
        if (myBtn) myBtn.textContent = originalText;
      } catch (e) {
        if (this.statusEl) {
          this.statusEl.textContent = 'No pudimos añadirlo: ' + (e.message || 'error desconocido');
          this.statusEl.className = 'eb-pw__status eb-pw__status--error';
        }
        buttons.forEach((b) => { b.disabled = false; });
        if (myBtn) myBtn.textContent = originalText;
      }
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }

  customElements.define('eb-pack-wizard', EBPackWizard);
})();
